import clsx from 'clsx';
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { RAG_LABEL, fmtMonthDay, type MemberDTO } from '@wd/shared';
import { TopBar } from '../../app/AppShell';
import { errorText } from '../../lib/api';
import { useLayout } from '../../lib/media';
import { useCreateProgram, useMe, useMembers, usePrograms } from '../../lib/queries';
import { Icon } from '../../ui/icons';
import { MenuList, Popover } from '../../ui/menu';
import { Avatar, Button, EmptyState, Modal, Skeleton } from '../../ui/primitives';
import '../projects/projectForm.css';
import './programs.css';

// 项目集列表：项目经理管的一组关键任务，每期对照计划出进度汇报。

export function ProgramsPage() {
  const mobile = useLayout() === 'mobile';
  const navigate = useNavigate();
  const { data: list, isLoading, error } = usePrograms();
  const [creating, setCreating] = useState(false);

  const newBtn = (
    <Button variant={mobile ? 'ghost' : 'primary'} icon="plus" mobile={mobile} onClick={() => setCreating(true)}>
      新建项目集
    </Button>
  );

  return (
    <>
      {!mobile && (
        <TopBar crumbs={['项目集']} icon="list">
          {newBtn}
        </TopBar>
      )}
      <div className={clsx('pg-list', mobile && 'mobile')}>
        {mobile && (
          <div className="pg-m-title">
            <span className="flex1">项目集</span>
            {newBtn}
          </div>
        )}
        {isLoading ? (
          <div style={{ padding: 24 }} aria-busy="true">
            <Skeleton w={260} h={14} />
          </div>
        ) : error ? (
          <EmptyState icon="wifioff" title="项目集没有加载出来" desc={errorText(error)} />
        ) : !list?.length ? (
          <EmptyState
            icon="list"
            title="还没有项目集"
            desc="项目经理把一组关键任务放进项目集，记下每个任务的计划时间。之后每期对照计划，AI 根据大家的日报起草进度汇报：本期进展、延期原因、上期计划完成情况、下期计划。"
          >
            <Button variant="primary" onClick={() => setCreating(true)}>
              新建项目集
            </Button>
          </EmptyState>
        ) : (
          <div className="pg-rows">
            {list.map((p) => (
              <button key={p.id} type="button" className={clsx('pg-row', p.status === 'closed' && 'closed')} onClick={() => navigate(`/programs/${p.id}`)}>
                <span className="code">{p.code}</span>
                <span className="name ellipsis">{p.name}</span>
                <span className="pm">
                  <Avatar initial={p.pm.initial} seed={p.pm.name} size={16} />
                  {p.pm.name}
                </span>
                <span className="stats">
                  {p.taskCount} 个关键任务
                  <span className="c4"> · 完成 {p.doneCount}</span>
                  {p.delayedCount > 0 && <span className="c-red"> · 延后 {p.delayedCount}</span>}
                  {p.blockedCount > 0 && <span className="c-red"> · 卡住 {p.blockedCount}</span>}
                </span>
                <span className="last">
                  {p.latestReport ? (
                    <>
                      <span className={clsx('pg-rag-dot', p.latestReport.rag)} />
                      {fmtMonthDay(p.latestReport.asOf)} {p.latestReport.status === 'draft' ? '草稿' : RAG_LABEL[p.latestReport.rag]}
                    </>
                  ) : (
                    <span className="c4">还没有汇报</span>
                  )}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
      {creating && <CreateProgramModal onClose={() => setCreating(false)} />}
    </>
  );
}

const eligible = (m: MemberDTO) => m.status === 'active' || m.status === 'invited';

export function CreateProgramModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const { data: members = [] } = useMembers();
  const create = useCreateProgram();
  const canPick = me?.role === 'admin' || me?.role === 'manager';
  const [name, setName] = useState('');
  const [pmId, setPmId] = useState(me?.id ?? '');
  const [sponsorId, setSponsorId] = useState(me?.managerId ?? '');
  const [teamId, setTeamId] = useState(me?.teamId ?? '');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [to, setTo] = useState<Set<string>>(new Set());
  const [picking, setPicking] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const byId = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);
  const people = members.filter(eligible);

  const save = async () => {
    setErr(null);
    try {
      const r = await create.mutateAsync({
        name,
        pmId: pmId || me!.id,
        sponsorId: sponsorId || null,
        teamId: teamId || null,
        startDate: startDate || null,
        endDate: endDate || null,
        reportToIds: [...to],
      });
      onClose();
      navigate(`/programs/${r.id}?tab=tasks`);
    } catch (e) {
      setErr(errorText(e));
    }
  };

  return (
    <Modal
      width={540}
      title="新建项目集"
      onClose={onClose}
      footNote="建好后把关键任务加进来，记下每个任务的计划时间"
      footer={
        <>
          <Button variant="text" onClick={onClose}>
            取消
          </Button>
          <Button variant="primary" disabled={name.trim().length < 2 || create.isPending} onClick={() => void save()}>
            新建
          </Button>
        </>
      }
    >
      <div className="pf-form">
        <label>
          <span className="k">名称</span>
          <input className="wd-input" autoFocus maxLength={40} value={name} placeholder="例如：MATIC 混合逆变器" onChange={(e) => setName(e.target.value)} />
        </label>
        <div className="pf-row">
          <label>
            <span className="k">项目经理</span>
            <select className="pf-select" value={pmId} disabled={!canPick} onChange={(e) => setPmId(e.target.value)}>
              {(canPick ? people : people.filter((m) => m.id === me?.id)).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                  {m.teamName ? ` · ${m.teamName}` : ''}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="k">决策人（可选）</span>
            <select className="pf-select" value={sponsorId} onChange={(e) => setSponsorId(e.target.value)}>
              <option value="">不设</option>
              {people.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="pf-row">
          <label>
            <span className="k">开始</span>
            <input className="wd-input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </label>
          <label>
            <span className="k">计划结束</span>
            <input className="wd-input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </label>
        </div>
        <label>
          <span className="k">团队（新建的关键任务默认归到这里）</span>
          <select className="pf-select" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
            <option value="">不指定</option>
            {(me?.teams ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
        <div className="pf-field">
          <span className="k">进度汇报发给（决策人会自动收到）</span>
          <div className="pf-people">
            {[...to].map((id) => {
              const m = byId.get(id);
              return (
                <button
                  key={id}
                  type="button"
                  className="pf-chip"
                  title="移除"
                  onClick={() =>
                    setTo((s) => {
                      const n = new Set(s);
                      n.delete(id);
                      return n;
                    })
                  }
                >
                  <Avatar initial={m?.initial ?? '?'} seed={m?.name} size={16} />
                  {m?.name ?? '已离开'}
                  <Icon name="close" size={12} />
                </button>
              );
            })}
            <button ref={addRef} type="button" className="pf-add" onClick={() => setPicking(true)}>
              <Icon name="plus" size={14} />
              添加
            </button>
          </div>
          <Popover anchor={addRef} open={picking} onClose={() => setPicking(false)} width={260}>
            <MenuList
              search
              items={people.filter((m) => m.id !== pmId).map((m) => ({ value: m.id, label: m.name, icon: <Avatar initial={m.initial} seed={m.name} />, hint: m.teamName ?? '' }))}
              selected={to}
              onPick={(id) =>
                setTo((s) => {
                  const n = new Set(s);
                  if (n.has(id)) n.delete(id);
                  else n.add(id);
                  return n;
                })
              }
            />
          </Popover>
        </div>
        {err && <div className="t-meta c-red">{err}</div>}
      </div>
    </Modal>
  );
}
